import { Button, Colors, Divider } from "@blueprintjs/core";

import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { isWorkspaceAdmin } from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import { CoachList } from "@/components/pages_logged_in/workspace-admin/CoachList";
import { MemberMeetingsList } from "@/components/pages_logged_in/workspace-admin/MemberMeetingsList";
import { MemberPermissionDisplay } from "@/components/pages_logged_in/workspace-admin/PermissionDisplay";
import {
  isAdminMember,
  MemberName,
} from "@/components/pages_logged_in/workspace-admin/WorkspaceMemberTable";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import { Avatar } from "@/components/pieces/users/Avatar";
import { StatsMember } from "@/types/workspaces";
import { ensureNonNullArray } from "@/utils/array";

import { CreditsWithAccountsPopover } from "./CreditsWithAccounts";
import { GroupsWithPopover } from "./GroupsWithPopover";

interface MemberDetailsDialogProps {
  member: StatsMember;
  open: boolean;
  onClose: () => void;
  onRemove: (member: StatsMember) => void;
  onTransferCredits: (member: StatsMember) => void;
  onSetGroup: (member: StatsMember) => void;
}

export function MemberDetailsDialog({
  member,
  open,
  onClose,
  onRemove,
  onTransferCredits,
  onSetGroup,
}: MemberDetailsDialogProps) {
  const { user, workspace, groups, ledgerAccounts } = member;
  const coaches = ensureNonNullArray(user?.coaches);
  const [group] = groups || [];

  return (
    <StyledDialog
      isOpen={open}
      onClose={() => onClose()}
      lazy={false}
      title={`User Details`}
      width="780px"
    >
      <Box py={24} px={24}>
        <Box>
          <Flex mb={3}>
            <Tooltip content={user.email}>
              <Avatar size={60} user={user} />
            </Tooltip>
            <Flex ml={2} flexDirection="column">
              <Box>
                <MemberName
                  style={{ fontSize: 24, fontWeight: 600 }}
                  row={member}
                />
              </Box>
              <Flex alignItems="center">
                <Box>{user.email}</Box>
                <Box mx={2}>·</Box>
                {ledgerAccounts && (
                  <CreditsWithAccountsPopover
                    ledgerAccounts={ensureNonNullArray(ledgerAccounts)}
                  />
                )}
                <Box mx={2}>·</Box>
                {groups && <GroupsWithPopover groups={groups} />}
              </Flex>
            </Flex>
          </Flex>
          <Flex>
            {isWorkspaceAdmin() && (
              <Button
                style={{ marginRight: 10 }}
                onClick={() => {
                  onTransferCredits(member);
                }}
              >
                Transfer Credits
              </Button>
            )}
            <Button
              style={{ marginRight: 10 }}
              onClick={() => {
                onSetGroup(member);
              }}
            >
              Set Group
            </Button>
            {isWorkspaceAdmin() && (
              <Button
                onClick={() => {
                  onRemove(member);
                }}
                style={{
                  color: !isAdminMember(member || {}) ? Colors.RED3 : undefined,
                }}
                disabled={isAdminMember(member || {})}
              >
                Remove User
              </Button>
            )}
          </Flex>
        </Box>
        <Box my={4}>
          <Divider />
        </Box>

        <MemberPermissionDisplay
          user={user}
          group={group}
          workspace={workspace}
        />

        <CoachList coaches={coaches} />

        <MemberMeetingsList user={user} />
      </Box>
    </StyledDialog>
  );
}
